import { useState, useEffect, useCallback } from "react";
import { useOrg } from "@/lib/orgContext";
import { useAuth, useClerk } from "@clerk/react";
import {
  useGetNotificationPreferences,
  useUpdateNotificationPreferences,
  getGetNotificationPreferencesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetOrgFiscalYear, useUpdateOrgFiscalYear, getGetOrgFiscalYearQueryKey } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { Check, Zap, Building2, CreditCard, ExternalLink, Loader2, Save, Shield, Star, LogOut, Bell, Send, Users, UserPlus, Download, Trash2, AlertTriangle, Database, Receipt, Clock, Trophy, Lock, Code2, KeyRound, Palette, Headphones, PlayCircle, ShieldAlert, ChevronRight, Minus, Plus, X, Wand2 } from "lucide-react";
import { PersonaBadge } from "@/components/PersonaBadge";
import type { Persona } from "@/lib/orgContext";
import { Link as WLink } from "wouter";
import { PLAN_META } from "@workspace/plans";
import { LockedPreview } from "@/components/LockedPreview";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ApprovalRulesSettings } from "@/components/ApprovalRulesSettings";
import { PolicyRulesSettings } from "@/components/PolicyRulesSettings";
import { SigningMethodsCard } from "@/components/SigningMethodsCard";
import { CustomFieldsSettings } from "@/components/CustomFieldsSettings";
import { useLocation } from "wouter";
import { useOnboarding } from "@/lib/onboarding";
import { setActivePersonaStorage } from "@/lib/activePersona";

const MAX_SEATS = 500;

interface TeamMember {
  id: number;
  email: string;
  role: "admin" | "viewer";
  name: string | null;
  invitedAt: string;
  acceptedAt: string | null;
  status: "active" | "pending";
  personas: Persona[];
}

const ALL_PERSONAS: Persona[] = ["Finance", "HR", "Legal", "AD", "SA"];
const PERSONA_LABEL: Record<Persona, string> = {
  Finance: "Finance",
  HR: "HR",
  Legal: "Legal",
  AD: "Athletic Director",
  SA: "Sport Administrator",
};

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const DIVISIONS = [
  "Division I (FBS)",
  "Division I (FCS)",
  "Division I (Other)",
  "Division II",
  "Division III",
  "NAIA",
  "NJCAA",
  "Other",
];

const PLAN_ICONS: Record<string, { icon: typeof Zap; color: string }> = {
  free: { icon: Trophy, color: "text-gray-500" },
  track: { icon: Zap, color: "text-blue-500" },
  draft: { icon: Star, color: "text-amber-500" },
  enterprise: { icon: Shield, color: "text-purple-500" },
};

const PLANS = (["free", "track", "draft", "enterprise"] as const).map((key) => {
  const meta = PLAN_META[key];
  const ic = PLAN_ICONS[key];
  let price: string;
  if (meta.annualPrice === null) {
    price = "From $150k/yr";
  } else if (meta.annualPrice === 0) {
    price = "$0";
  } else {
    price = `$${meta.annualPrice.toLocaleString()}/yr`;
  }
  return {
    key,
    name: meta.name,
    price,
    description: meta.description,
    icon: ic.icon,
    iconColor: ic.color,
    features: meta.highlights,
  };
});

const APPROVAL_EMAIL_STAGE_LABELS: Record<string, string> = {
  sport_admin_review: "Sport Admin Review",
  ad_review: "AD Review",
  compliance_review: "Compliance Review",
  legal_review: "Legal Review",
};

interface ApprovalEmailPreferences {
  approvalEmailsEnabled: boolean;
  approvalEmailStages: string[] | null;
  approvalEmailsOnlySoleApprover: boolean;
  availableStages: string[];
}

function FiscalYearCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useGetOrgFiscalYear();
  const update = useUpdateOrgFiscalYear();
  const current = (data as { fiscalYearStartMonth?: number } | undefined)?.fiscalYearStartMonth ?? 1;
  const [month, setMonth] = useState<number>(current);
  useEffect(() => { setMonth(current); }, [current]);
  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  async function save() {
    try {
      await update.mutateAsync({ data: { fiscalYearStartMonth: month } });
      await qc.invalidateQueries({ queryKey: getGetOrgFiscalYearQueryKey() });
      toast({ title: "Fiscal year updated", description: `Year now starts in ${months[month - 1]}.` });
    } catch (e) {
      handleApiError(toast, e);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Fiscal year
        </CardTitle>
        <CardDescription>
          Used by Finance reports and compensation totals to bucket spend by your reporting calendar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5 max-w-xs">
          <Label htmlFor="fy-month">Fiscal year starts in</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))} disabled={isLoading}>
            <SelectTrigger id="fy-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m, i) => (
                <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} disabled={update.isPending || month === current} size="sm">
          {update.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function MyBulkDownloadEmailPreference() {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const token = await getToken();
      const res = await fetch("/api/me/bulk-download-email-preferences", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setLoadError(true);
        return;
      }
      const data = (await res.json()) as { bulkDownloadEmailsEnabled: boolean };
      setEnabled(data.bulkDownloadEmailsEnabled);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(next: boolean) {
    if (enabled === null) return;
    setSaving(true);
    const previous = enabled;
    setEnabled(next);
    try {
      const token = await getToken();
      const res = await fetch("/api/me/bulk-download-email-preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bulkDownloadEmailsEnabled: next }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        bulkDownloadEmailsEnabled?: boolean;
        error?: string;
      };
      if (!res.ok) {
        setEnabled(previous);
        toast({
          title: "Could not update preference",
          description: data.error ?? "Please try again.",
          variant: "destructive",
        });
        return;
      }
      setEnabled(data.bulkDownloadEmailsEnabled ?? next);
      toast({
        title: next ? "Bulk download emails enabled" : "Bulk download emails disabled",
        description: next
          ? "You'll get an email when a large bulk download is ready."
          : "We'll skip the email — keep the contracts page open to download instead.",
      });
    } catch {
      setEnabled(previous);
      toast({ title: "Network error", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your bulk download preference…
      </div>
    );
  }

  if (loadError || enabled === null) {
    return (
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        data-testid="my-bulk-download-email-preference-error"
      >
        <div>
          <p className="text-sm font-medium">Bulk download ready emails</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Couldn't load your preference. You can retry below.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          data-testid="retry-load-bulk-download-emails"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      data-testid="my-bulk-download-email-preference"
    >
      <div>
        <p className="text-sm font-medium">Bulk download ready emails</p>
        <p className="text-sm text-muted-foreground mt-0.5">
          {enabled
            ? "Get an email when a large bulk contract export is ready to download."
            : "We won't email you when bulk downloads finish — check the contracts page for the ready download."}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge
          variant={enabled ? "default" : "secondary"}
          className={enabled ? "bg-green-500/10 text-green-700 border-green-200" : ""}
        >
          {enabled ? "On" : "Off"}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          disabled={saving}
          onClick={() => toggle(!enabled)}
          data-testid="toggle-my-bulk-download-emails"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {enabled ? "Disable" : "Enable"}
        </Button>
      </div>
    </div>
  );
}

function MyApprovalEmailPreferences() {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [prefs, setPrefs] = useState<ApprovalEmailPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch("/api/me/approval-email-preferences", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as ApprovalEmailPreferences;
        if (!cancelled) setPrefs(data);
      } catch {
        /* ignore — section just stays in loading state */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  async function update(patch: Partial<ApprovalEmailPreferences>) {
    if (!prefs) return;
    setSaving(true);
    const previous = prefs;
    const optimistic = { ...prefs, ...patch };
    setPrefs(optimistic);
    try {
      const token = await getToken();
      const res = await fetch("/api/me/approval-email-preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<ApprovalEmailPreferences> & { error?: string };
      if (!res.ok) {
        setPrefs(previous);
        toast({
          title: "Could not update preferences",
          description: data.error ?? "Please try again.",
          variant: "destructive",
        });
        return;
      }
      setPrefs({
        approvalEmailsEnabled: data.approvalEmailsEnabled ?? optimistic.approvalEmailsEnabled,
        approvalEmailStages: data.approvalEmailStages ?? null,
        approvalEmailsOnlySoleApprover:
          data.approvalEmailsOnlySoleApprover ?? optimistic.approvalEmailsOnlySoleApprover,
        availableStages: data.availableStages ?? optimistic.availableStages,
      });
      toast({ title: "Preferences saved" });
    } catch {
      setPrefs(previous);
      toast({ title: "Network error", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  function toggleStage(stage: string, checked: boolean) {
    if (!prefs) return;
    const current = prefs.approvalEmailStages ?? prefs.availableStages.slice();
    const next = checked ? [...new Set([...current, stage])] : current.filter((s) => s !== stage);
    if (next.length === prefs.availableStages.length) {
      void update({ approvalEmailStages: null });
    } else {
      void update({ approvalEmailStages: next });
    }
  }

  if (loading || !prefs) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your notification preferences…
      </div>
    );
  }

  const effectiveStages = prefs.approvalEmailStages ?? prefs.availableStages;
  const restrictedToSubset = prefs.approvalEmailStages !== null;

  return (
    <div className="space-y-4" data-testid="my-approval-email-preferences">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm font-medium">My approval emails</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {prefs.approvalEmailsEnabled
              ? "You'll be emailed when contracts arrive in your approval queue."
              : "You won't receive any approval inbox emails — even if your org has them on."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={prefs.approvalEmailsEnabled ? "default" : "secondary"}
            className={prefs.approvalEmailsEnabled ? "bg-green-500/10 text-green-700 border-green-200" : ""}
          >
            {prefs.approvalEmailsEnabled ? "On" : "Off"}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            disabled={saving}
            onClick={() => update({ approvalEmailsEnabled: !prefs.approvalEmailsEnabled })}
            data-testid="toggle-my-approval-emails"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {prefs.approvalEmailsEnabled ? "Disable" : "Enable"}
          </Button>
        </div>
      </div>

      {prefs.approvalEmailsEnabled && (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Stages I want emails for
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {restrictedToSubset
                ? `Only the stages checked below will email you. Re-check all to receive every stage.`
                : "Currently receiving every stage. Uncheck any to silence them."}
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {prefs.availableStages.map((stage) => {
                const checked = effectiveStages.includes(stage);
                return (
                  <label
                    key={stage}
                    className="flex items-center gap-2 text-sm cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input"
                      checked={checked}
                      disabled={saving}
                      onChange={(e) => toggleStage(stage, e.target.checked)}
                      data-testid={`stage-checkbox-${stage}`}
                    />
                    {APPROVAL_EMAIL_STAGE_LABELS[stage] ?? stage}
                  </label>
                );
              })}
            </div>
          </div>

          <Separator />

          <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input mt-0.5"
              checked={prefs.approvalEmailsOnlySoleApprover}
              disabled={saving}
              onChange={(e) => update({ approvalEmailsOnlySoleApprover: e.target.checked })}
              data-testid="toggle-only-sole-approver"
            />
            <span>
              <span className="font-medium">Only when I'm the sole approver</span>
              <span className="block text-xs text-muted-foreground">
                Skip the email when other teammates can approve the same stage.
              </span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

const NOTIF_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  approval: { label: "Approvals", description: "When a contract enters your approval queue." },
  mention: { label: "Mentions", description: "When someone @mentions you in a comment." },
  renewal: { label: "Renewals", description: "Renewal target date reminders assigned to you." },
  expiring_contract: { label: "Expiring Contracts", description: "Contracts expiring within 30 days." },
  expiring_insurance: { label: "Expiring Insurance", description: "Insurance policies expiring within 30 days." },
  billing: { label: "Billing", description: "Trial endings, seat limits, and payment issues." },
  system: { label: "System", description: "Platform announcements and system updates." },
};

const ALL_NOTIF_TYPES = Object.keys(NOTIF_TYPE_LABELS) as Array<keyof typeof NOTIF_TYPE_LABELS>;

function NotificationPreferences() {
  const queryClient = useQueryClient();
  const prefsKey = getGetNotificationPreferencesQueryKey();
  const { data: prefs, isLoading } = useGetNotificationPreferences({
    query: { queryKey: prefsKey, staleTime: 60_000 },
  });
  const { mutate: updatePrefs, isPending } = useUpdateNotificationPreferences({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: prefsKey }),
    },
  });
  const { toast } = useToast();

  function isInAppEnabled(type: string): boolean {
    const match = prefs?.find((p) => p.type === type);
    return match === undefined ? true : match.inAppEnabled;
  }

  function isEmailEnabled(type: string): boolean {
    const match = prefs?.find((p) => p.type === type);
    return match === undefined ? true : match.emailEnabled;
  }

  function handleToggle(type: string, channel: "inApp" | "email", value: boolean) {
    const current = ALL_NOTIF_TYPES.map((t) => ({
      type: t as "approval" | "mention" | "renewal" | "expiring_contract" | "expiring_insurance" | "billing" | "system",
      inAppEnabled: t === type && channel === "inApp" ? value : isInAppEnabled(t),
      emailEnabled: t === type && channel === "email" ? value : isEmailEnabled(t),
    }));
    updatePrefs(
      { data: { preferences: current } },
      {
        onError: () =>
          toast({ title: "Could not update preferences", description: "Please try again.", variant: "destructive" }),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading preferences…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Column headers */}
      <div className="flex items-center justify-end gap-6 pr-1">
        <span className="text-xs font-medium text-muted-foreground w-14 text-center">In-App</span>
        <span className="text-xs font-medium text-muted-foreground w-14 text-center">Email</span>
      </div>
      {ALL_NOTIF_TYPES.map((type, i) => {
        const inApp = isInAppEnabled(type);
        const email = isEmailEnabled(type);
        const info = NOTIF_TYPE_LABELS[type];
        return (
          <div key={type}>
            {i > 0 && <Separator className="my-1" />}
            <div className="flex items-center justify-between gap-4 py-1">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{info.label}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{info.description}</p>
              </div>
              <div className="flex items-center gap-6 flex-shrink-0">
                {/* In-app toggle */}
                <div className="w-14 flex justify-center">
                  <Switch
                    checked={inApp}
                    disabled={isPending}
                    onCheckedChange={(v) => handleToggle(type, "inApp", v)}
                    aria-label={`${info.label} in-app notifications`}
                  />
                </div>
                {/* Email toggle */}
                <div className="w-14 flex justify-center">
                  <Switch
                    checked={email}
                    disabled={isPending}
                    onCheckedChange={(v) => handleToggle(type, "email", v)}
                    aria-label={`${info.label} email notifications`}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface PersonaRouteRow {
  type: string;
  personas: string[] | null;
  isOverride: boolean;
  defaultPersonas: string[] | null;
}

function NotificationPersonaRoutingCard() {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [routes, setRoutes] = useState<PersonaRouteRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/notifications/persona-routes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("load_failed");
      const data = await res.json() as { routes: PersonaRouteRow[] };
      setRoutes(data.routes);
    } catch {
      toast({ title: "Could not load persona routing", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [getToken, toast]);

  useEffect(() => { void load(); }, [load]);

  async function save(next: PersonaRouteRow[]) {
    setSaving(true);
    const previous = routes;
    setRoutes(next);
    try {
      const token = await getToken();
      const res = await fetch("/api/notifications/persona-routes", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          routes: next.map((r) => ({ type: r.type, personas: r.personas })),
        }),
      });
      if (!res.ok) throw new Error("save_failed");
      const data = await res.json() as { routes: PersonaRouteRow[] };
      setRoutes(data.routes);
      toast({ title: "Routing updated" });
    } catch {
      setRoutes(previous);
      toast({ title: "Could not update routing", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/notifications/persona-routes/reset", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("reset_failed");
      const data = await res.json() as { routes: PersonaRouteRow[] };
      setRoutes(data.routes);
      toast({ title: "Routing reset to defaults" });
    } catch {
      toast({ title: "Could not reset routing", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  function togglePersona(type: string, persona: Persona, on: boolean) {
    if (!routes) return;
    const next = routes.map((r) => {
      if (r.type !== type) return r;
      const current = r.personas ?? ALL_PERSONAS.slice();
      const set = new Set(current);
      if (on) set.add(persona); else set.delete(persona);
      return { ...r, personas: Array.from(set), isOverride: true };
    });
    void save(next);
  }

  function toggleUniversal(type: string, on: boolean) {
    if (!routes) return;
    const next = routes.map((r) => {
      if (r.type !== type) return r;
      return { ...r, personas: on ? null : [], isOverride: true };
    });
    void save(next);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Persona routing
        </CardTitle>
        <CardDescription>
          Choose which personas receive each kind of in-app notification. Org admins
          with no persona always receive everything. Universal types (e.g. @mentions,
          system announcements) ignore persona filters.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading || !routes ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {routes.map((r, i) => {
                const info = NOTIF_TYPE_LABELS[r.type] ?? { label: r.type, description: "" };
                const universal = r.personas === null;
                return (
                  <div key={r.type} data-testid={`persona-route-${r.type}`}>
                    {i > 0 && <Separator className="my-2" />}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium flex items-center gap-2">
                          {info.label}
                          {r.isOverride && <Badge variant="secondary" className="text-[10px]">Custom</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{info.description}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5"
                            checked={universal}
                            disabled={saving}
                            onChange={(e) => toggleUniversal(r.type, e.target.checked)}
                            data-testid={`persona-route-universal-${r.type}`}
                          />
                          Everyone
                        </label>
                        {!universal && ALL_PERSONAS.map((p) => {
                          const active = (r.personas ?? []).includes(p);
                          return (
                            <label
                              key={p}
                              className="flex items-center gap-1 text-xs cursor-pointer rounded border px-2 py-0.5"
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5"
                                checked={active}
                                disabled={saving}
                                onChange={(e) => togglePersona(r.type, p, e.target.checked)}
                                data-testid={`persona-route-${r.type}-${p}`}
                              />
                              {PERSONA_LABEL[p]}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Separator />
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => void reset()} disabled={saving}>
                Reset to defaults
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Task #555: per-member opt-in toggle for the Legal Drafting Wizard.
 * Persists to /api/auth/drafting-wizard-prefs (server) plus a localStorage
 * mirror so the clause editor's entry button shows up immediately on the
 * next page load even before the API responds.
 */
function DraftingWizardPrefCard() {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch("/api/auth/drafting-wizard-prefs", {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) return;
        const j = (await r.json()) as { enabled?: boolean };
        if (cancelled) return;
        const en = Boolean(j.enabled);
        setEnabled(en);
        try { window.localStorage.setItem("zindeal.draftingWizardEnabled", en ? "true" : "false"); } catch { /* ignore */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const save = async (next: boolean) => {
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch("/api/auth/drafting-wizard-prefs", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) {
        toast({ title: "Couldn't save", description: "Please try again.", variant: "destructive" });
        return;
      }
      setEnabled(next);
      try { window.localStorage.setItem("zindeal.draftingWizardEnabled", next ? "true" : "false"); } catch { /* ignore */ }
      toast({ title: next ? "Drafting wizard enabled" : "Drafting wizard disabled" });
    } catch {
      toast({ title: "Network error", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wand2 className="h-5 w-5" />
          Drafting wizard (Legal)
        </CardTitle>
        <CardDescription>
          Surfaces a step-by-step wizard on the clause editor that drafts a cited skeleton from your house library, jurisdiction statutes, and federal authority. Every paragraph is grounded in a database source — never an LLM guess.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={enabled}
            disabled={loading || saving}
            onChange={(e) => save(e.target.checked)}
            data-testid="checkbox-drafting-wizard-enabled"
          />
          <span className="text-sm">Enable the drafting wizard for me</span>
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </label>
      </CardContent>
    </Card>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function SportsInput({ memberId, disabled }: { memberId: number; disabled: boolean }) {
  const [sports, setSports] = useState<string[] | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${basePath}/api/team/members/${memberId}/sports`, { credentials: "include" });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setSports(Array.isArray(j.sports) ? j.sports : []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [memberId]);

  async function save(next: string[]) {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${basePath}/api/team/members/${memberId}/sports`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sports: next }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `Failed (${r.status})`);
      }
      const j = await r.json();
      setSports(j.sports ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function add() {
    const v = draft.trim();
    if (!v) return;
    const current = sports ?? [];
    if (current.includes(v)) { setDraft(""); return; }
    save([...current, v]);
    setDraft("");
  }

  function remove(s: string) {
    const current = sports ?? [];
    save(current.filter((x) => x !== s));
  }

  return (
    <div className="mt-2 pl-1">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
        Sports {(sports?.length ?? 0) === 0 && "(empty = all sports)"}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {(sports ?? []).map((s) => (
          <span key={s} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700">
            {s}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(s)}
                disabled={saving}
                className="text-amber-700/70 hover:text-amber-900 disabled:opacity-50"
                aria-label={`Remove ${s}`}
              >×</button>
            )}
          </span>
        ))}
        {!disabled && (
          <span className="inline-flex items-center gap-1">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="Add sport…"
              disabled={saving}
              className="text-xs px-2 py-0.5 rounded border border-border bg-background w-28"
            />
            <button
              type="button"
              onClick={add}
              disabled={saving || !draft.trim()}
              className="text-xs px-2 py-0.5 rounded border border-border hover:bg-muted disabled:opacity-50"
            >Add</button>
          </span>
        )}
      </div>
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

export default function Settings() {
  const { org, refetch } = useOrg();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const onboarding = useOnboarding();
  const [clearingSample, setClearingSample] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);

  const [institutionName, setInstitutionName] = useState("");
  const [division, setDivision] = useState("");
  const [conference, setConference] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [seatsByPlan, setSeatsByPlan] = useState<Record<string, number>>({
    track: PLAN_META.track.minSeats,
    draft: PLAN_META.draft.minSeats,
  });
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [approvalNotifEnabled, setApprovalNotifEnabled] = useState(true);
  const [togglingApprovalNotif, setTogglingApprovalNotif] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [togglingAlerts, setTogglingAlerts] = useState(false);
  const [sendingTestAlert, setSendingTestAlert] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ downloadUrl: string; expiresAt: string; sizeBytes: number; emailedTo: string | null; pending: boolean } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "viewer">("viewer");
  const [inviting, setInviting] = useState(false);
  const [updatingPersonas, setUpdatingPersonas] = useState<number | null>(null);

  const [savingDefaultPersona, setSavingDefaultPersona] = useState(false);

  const isAdmin = org?.role === "admin";

  const fetchTeam = useCallback(async () => {
    setTeamLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/team/members", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json() as TeamMember[];
        setTeamMembers(data);
      }
    } catch {
      /* ignore */
    } finally {
      setTeamLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (org) {
      setInstitutionName(org.institutionName ?? org.name ?? "");
      setDivision(org.division ?? "");
      setConference(org.conference ?? "");
      setAlertsEnabled(org.emailAlertsEnabled ?? true);
      setApprovalNotifEnabled(org.approvalNotificationsEnabled ?? true);
      setAlertEmail(org.alertEmail ?? "");
      setDirty(false);
      fetchTeam();
    }
  }, [org, fetchTeam]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "success") {
      // Fetch the freshest org so we can echo the purchased seat count in
      // the activation toast (task #115). Falls back to a generic message
      // if the lookup fails.
      (async () => {
        try {
          const token = await getToken();
          const res = await fetch("/api/auth/org", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json() as { purchasedSeats?: number };
            const seats = typeof data.purchasedSeats === "number" ? data.purchasedSeats : null;
            if (seats && seats > 0) {
              toast({
                title: "Subscription activated!",
                description: `${seats} seat${seats === 1 ? "" : "s"} activated. Check your inbox for a receipt.`,
              });
            } else {
              toast({ title: "Subscription activated!", description: "Your plan is now active." });
            }
          } else {
            toast({ title: "Subscription activated!", description: "Your plan is now active." });
          }
        } catch {
          toast({ title: "Subscription activated!", description: "Your plan is now active." });
        }
        refetch();
      })();
      setLocation("/settings", { replace: true });
    } else if (params.get("billing") === "cancelled") {
      toast({ title: "Checkout cancelled", description: "Your plan was not changed.", variant: "destructive" });
      setLocation("/settings", { replace: true });
    }
  }, []);

  function markDirty() {
    setDirty(true);
  }

  async function handleSave() {
    if (!institutionName.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/auth/org", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ institutionName, division, conference, alertEmail }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `Server error ${res.status}`);
      }
      await refetch();
      setDirty(false);
      toast({ title: "Institution updated", description: "Your profile changes have been saved." });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err instanceof Error ? err.message : "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleAlerts(enabled: boolean) {
    setTogglingAlerts(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/alerts/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emailAlertsEnabled: enabled }),
      });
      if (!res.ok) throw new Error("Failed to update");
      setAlertsEnabled(enabled);
      await refetch();
      toast({
        title: enabled ? "Email alerts enabled" : "Email alerts disabled",
        description: enabled
          ? "You'll receive daily digest emails for expiring contracts and insurance."
          : "You will no longer receive alert emails.",
      });
    } catch {
      toast({ title: "Update failed", description: "Could not update alert settings.", variant: "destructive" });
    } finally {
      setTogglingAlerts(false);
    }
  }

  async function handleToggleApprovalNotifications(enabled: boolean) {
    setTogglingApprovalNotif(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/alerts/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ approvalNotificationsEnabled: enabled }),
      });
      if (!res.ok) throw new Error("Failed to update");
      setApprovalNotifEnabled(enabled);
      await refetch();
      toast({
        title: enabled ? "Approval notifications enabled" : "Approval notifications disabled",
        description: enabled
          ? "Approvers will receive an email when a contract enters their queue."
          : "Approvers will no longer receive inbox notification emails.",
      });
    } catch {
      toast({ title: "Update failed", description: "Could not update approval notifications.", variant: "destructive" });
    } finally {
      setTogglingApprovalNotif(false);
    }
  }

  async function handleSendTestAlert() {
    setSendingTestAlert(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/alerts/test", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as { message?: string; email?: string; error?: string };
      if (!res.ok) {
        toast({ title: "Test failed", description: data.error ?? "Could not send test alert.", variant: "destructive" });
        return;
      }
      toast({ title: "Test alert sent", description: data.message ?? `Sent to ${data.email}` });
    } catch {
      toast({ title: "Test failed", description: "Network error.", variant: "destructive" });
    } finally {
      setSendingTestAlert(false);
    }
  }

  async function handleInvite() {
    if (!inviteEmail.trim() || !inviteEmail.includes("@")) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setInviting(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/team/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) {
        toast({ title: "Invite failed", description: data.error ?? "Could not send invite.", variant: "destructive" });
        return;
      }
      toast({ title: "Invitation sent", description: `${inviteEmail} has been invited as ${inviteRole}.` });
      setInviteEmail("");
      setInviteRole("viewer");
      await fetchTeam();
    } catch {
      toast({ title: "Invite failed", description: "Network error.", variant: "destructive" });
    } finally {
      setInviting(false);
    }
  }

  async function handleSaveDefaultPersona(value: Persona | null) {
    setSavingDefaultPersona(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/auth/me/active-persona", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ persona: value }),
      });
      const data = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) {
        toast({
          title: "Couldn't update default persona",
          description: data.error ?? "Please try again.",
          variant: "destructive",
        });
        return;
      }
      // Sync the sidebar switcher and any other listeners (writes to localStorage
      // and dispatches the change event). Skip the helper's internal remote PATCH
      // since we already persisted to the server above.
      setActivePersonaStorage(value, { skipRemote: true });
      await refetch();
      toast({
        title: value ? "Default persona saved" : "Default persona cleared",
        description: value
          ? `${value} will load by default when you sign in.`
          : "You'll start without an active persona on your next sign-in.",
      });
    } catch {
      toast({
        title: "Couldn't update default persona",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingDefaultPersona(false);
    }
  }

  async function handleUpdatePersonas(memberId: number, personas: Persona[]) {
    setUpdatingPersonas(memberId);
    try {
      const token = await getToken();
      const res = await fetch(`/api/team/members/${memberId}/personas`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ personas }),
      });
      const data = await res.json() as { personas?: Persona[]; error?: string };
      if (!res.ok) {
        toast({ title: "Update failed", description: data.error ?? "Could not update personas.", variant: "destructive" });
        return;
      }
      setTeamMembers((prev) =>
        prev.map((m) => m.id === memberId ? { ...m, personas: data.personas ?? personas } : m)
      );
      toast({ title: "Personas updated", description: "The member's personas have been saved." });
    } catch {
      toast({ title: "Update failed", description: "Network error.", variant: "destructive" });
    } finally {
      setUpdatingPersonas(null);
    }
  }

  async function handleExportData() {
    setExporting(true);
    setExportResult(null);
    try {
      const token = await getToken();
      const res = await fetch("/api/privacy/export", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as { downloadUrl?: string; expiresAt?: string; sizeBytes?: number; emailedTo?: string | null; pending?: boolean; error?: string };
      if (!res.ok || !data.downloadUrl || !data.expiresAt) {
        toast({ title: "Export failed", description: data.error ?? "Could not generate export.", variant: "destructive" });
        return;
      }
      setExportResult({
        downloadUrl: data.downloadUrl,
        expiresAt: data.expiresAt,
        sizeBytes: data.sizeBytes ?? 0,
        emailedTo: data.emailedTo ?? null,
        pending: data.pending === true,
      });
      toast({
        title: data.pending ? "Export queued" : "Export ready",
        description: data.pending
          ? (data.emailedTo
              ? `Your export is being prepared. We'll email the download link to ${data.emailedTo} when it's ready.`
              : "Your export is being prepared. The download link will be available shortly.")
          : (data.emailedTo
              ? `Download link is ready below and was also sent to ${data.emailedTo}.`
              : "Your download link is ready below."),
      });
    } catch {
      toast({ title: "Export failed", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  }

  async function handleDeleteAccount() {
    const expected = org?.institutionName ?? org?.name ?? "";
    if (deleteConfirm.trim() !== expected) {
      toast({
        title: "Confirmation does not match",
        description: `Type "${expected}" exactly to confirm deletion.`,
        variant: "destructive",
      });
      return;
    }
    setDeleting(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/privacy/account", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirmation: deleteConfirm.trim() }),
      });
      const data = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) {
        toast({ title: "Deletion failed", description: data.error ?? "Could not delete account.", variant: "destructive" });
        setDeleting(false);
        return;
      }
      toast({ title: "Account deleted", description: "Your account and all data have been permanently removed." });
      setTimeout(() => {
        signOut({ redirectUrl: basePath || "/" });
      }, 800);
    } catch {
      toast({ title: "Deletion failed", description: "Network error. Please try again.", variant: "destructive" });
      setDeleting(false);
    }
  }

  async function handleUpgrade(plan: string) {
    if (!org) return;
    setLoadingPlan(plan);
    const meta = (plan === "track" || plan === "draft") ? PLAN_META[plan] : null;
    const minSeats = meta?.minSeats ?? 1;
    const requestedSeats = seatsByPlan[plan] ?? minSeats;
    const seats = Math.max(minSeats, Math.min(MAX_SEATS, Math.floor(Number(requestedSeats) || minSeats)));
    try {
      const token = await getToken();
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, seats }),
      });
      const data = await res.json() as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        toast({
          title: "Error",
          description: data.error ?? "Could not start checkout.",
          variant: "destructive",
        });
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not start checkout", fallbackDescription: "Network error. Please try again." });
    } finally {
      setLoadingPlan(null);
    }
  }

  async function handleManageBilling() {
    if (!org) return;
    setLoadingPortal(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/billing/portal", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        toast({
          title: "Error",
          description: data.error ?? "Could not open billing portal.",
          variant: "destructive",
        });
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not open billing portal", fallbackDescription: "Network error. Please try again." });
    } finally {
      setLoadingPortal(false);
    }
  }

  const currentPlan = org?.plan ?? "free";
  // Demo orgs (Rhino State sandbox) must not see pricing or upgrade CTAs —
  // the plan cards leak our pricing strategy during live sales demos. The
  // whole Plans grid and the Stripe BillingCard are swapped for a neutral
  // "your Zindeal account team manages billing" card. Sourced from the
  // server-trusted organizations.is_demo flag.
  const isDemoOrg = Boolean(org?.isDemo);

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your institution profile, team members, and billing.</p>
      </div>

      {/* Quick navigation — surfaces the most-asked-for sections so admins
          (especially Approval Rules) don't have to scroll the long settings
          page to find them. */}
      <Card data-testid="settings-quicknav">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-muted-foreground mr-1">Jump to:</span>
            <a href="#institution" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Institution</a>
            <a href="#branding" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Branding</a>
            <a href="#signing-methods" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Signing methods</a>
            <a
              href="#approval-rules"
              className="rounded-md border border-primary/30 bg-primary/5 px-2 py-1 font-medium text-primary hover:bg-primary/10"
              data-testid="settings-quicknav-approval-rules"
            >
              Approval rules
            </a>
            <a href="#policy-rules" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Policy rules</a>
            <a href="#team" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Team</a>
            <a href="#notifications" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Notifications</a>
            {!isDemoOrg && (
              <a href="#plans" className="rounded-md border bg-background px-2 py-1 hover:bg-muted">Plans</a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Institution Section */}
      <div id="institution" />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Institution
          </CardTitle>
          <CardDescription>Update your athletic program's details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="institutionName">
              Institution Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="institutionName"
              value={institutionName}
              onChange={(e) => { setInstitutionName(e.target.value); markDirty(); }}
              placeholder="e.g. Westfield University"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="division">Division / Level</Label>
            <select
              id="division"
              value={division}
              onChange={(e) => { setDivision(e.target.value); markDirty(); }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <option value="">Select division…</option>
              {DIVISIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="conference">
              Conference <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="conference"
              value={conference}
              onChange={(e) => { setConference(e.target.value); markDirty(); }}
              placeholder="e.g. Big Ten, SEC, Pac-12…"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Current plan</span>
              <Badge variant="secondary" className="capitalize">{currentPlan}</Badge>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || !dirty || !institutionName.trim()}
              className="bg-[#0B1834] hover:bg-[#0A152E] text-white"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Save changes</>
              )}
            </Button>
          </div>

        </CardContent>
      </Card>

      {isAdmin && <FiscalYearCard />}

      {/* Default Persona Section */}
      {(org?.personas ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Default persona
            </CardTitle>
            <CardDescription>
              Pick which of your assigned personas should load when you sign in. The sidebar persona switcher will reflect this immediately.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Select
                value={org?.lastActivePersona ?? "__none__"}
                onValueChange={(v) =>
                  handleSaveDefaultPersona(v === "__none__" ? null : (v as Persona))
                }
                disabled={savingDefaultPersona}
              >
                <SelectTrigger className="w-full sm:w-[260px]" data-testid="select-default-persona">
                  <SelectValue placeholder="Select a default persona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default</SelectItem>
                  {(org?.personas ?? []).map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savingDefaultPersona && (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {(org?.personas ?? []).length === 1
                ? "You're only assigned to one persona, so it's always active by default. Choose \"No default\" to start without an active persona."
                : "Choose \"No default\" to clear your saved preference and start without an active persona."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Drafting Wizard opt-in (Task #555) — only visible to Legal personas. */}
      {(org?.personas ?? []).includes("Legal") && <DraftingWizardPrefCard />}

      {/* Billing Section — demo orgs get a neutral "your account team
          manages billing" card instead of the Stripe portal/plan controls,
          so live sales demos never expose pricing or a self-serve upgrade. */}
      {isDemoOrg ? (
        <Card data-testid="billing-demo-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Billing is managed by your Zindeal account team. Reach out to your
              rep for plan changes.
            </p>
          </CardContent>
        </Card>
      ) : (
        <BillingCard
          org={org}
          loadingPortal={loadingPortal}
          onManageBilling={handleManageBilling}
        />
      )}

      {/* Email Alerts Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Email Alerts
          </CardTitle>
          <CardDescription>
            Receive daily digest emails when contracts or insurance policies are expiring within 30 days.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Daily expiry digest</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {alertsEnabled
                  ? "Alerts are active — you'll be notified of items expiring within 30 days."
                  : "Alerts are disabled — you won't receive any expiry notifications."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={alertsEnabled ? "default" : "secondary"} className={alertsEnabled ? "bg-green-500/10 text-green-700 border-green-200" : ""}>
                {alertsEnabled ? "On" : "Off"}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleToggleAlerts(!alertsEnabled)}
                disabled={togglingAlerts}
              >
                {togglingAlerts && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {alertsEnabled ? "Disable" : "Enable"}
              </Button>
            </div>
          </div>

          <Separator />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="approval-notifications-toggle">
            <div>
              <p className="text-sm font-medium">Approval inbox notifications</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {approvalNotifEnabled
                  ? "Approvers receive an email each time a contract advances into their queue."
                  : "Approvers won't receive emails when contracts arrive in their queue."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={approvalNotifEnabled ? "default" : "secondary"} className={approvalNotifEnabled ? "bg-green-500/10 text-green-700 border-green-200" : ""}>
                {approvalNotifEnabled ? "On" : "Off"}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleToggleApprovalNotifications(!approvalNotifEnabled)}
                disabled={togglingApprovalNotif}
                data-testid="toggle-approval-notifications"
              >
                {togglingApprovalNotif && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {approvalNotifEnabled ? "Disable" : "Enable"}
              </Button>
            </div>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="alertEmail">
              Alert email <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="alertEmail"
              type="email"
              value={alertEmail}
              onChange={(e) => { setAlertEmail(e.target.value); markDirty(); }}
              placeholder="compliance@university.edu"
            />
            <p className="text-xs text-muted-foreground">
              Where digest emails are delivered. Leave blank to use your account's primary email.
              Save changes in the Institution section above to apply.
            </p>
          </div>

          <Separator />

          <MyApprovalEmailPreferences />

          <Separator />

          <MyBulkDownloadEmailPreference />

          <Separator />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Send test alert</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Trigger a test run now to verify your email is set up correctly.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendTestAlert}
              disabled={sendingTestAlert}
            >
              {sendingTestAlert ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {sendingTestAlert ? "Sending…" : "Send test alert"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences Section */}
      <div id="notifications" />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Control which events reach you via the in-app bell and by email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <NotificationPreferences />
        </CardContent>
      </Card>

      {/* Persona routing (admin-only) */}
      {isAdmin && (
        <>
          <div id="persona-routing" />
          <NotificationPersonaRoutingCard />
        </>
      )}

      {/* Branding Section */}
      <div id="branding" />
      <BrandingCard isAdmin={isAdmin} org={org} onRefetch={refetch} />

      {/* Signing Methods Section */}
      <div id="signing-methods" />
      <SigningMethodsCard isAdmin={isAdmin} />

      {/* Approval Rules Section */}
      <div id="approval-rules" />
      <ApprovalRulesSettings isAdmin={isAdmin} />

      {/* Policy Rules Section */}
      <div id="policy-rules" />
      <PolicyRulesSettings isAdmin={isAdmin} />

      {/* Custom Fields Section */}
      <CustomFieldsSettings />

      {/* Team Section */}
      <div id="team" />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team
          </CardTitle>
          <CardDescription>
            Manage who has access to your organization. Admins can make changes; Viewers have read-only access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Your role:</span>
            <Badge variant={isAdmin ? "default" : "secondary"} className={isAdmin ? "bg-[#0B1834]/10 text-[#0B1834] border-[#0B1834]/20" : ""}>
              {isAdmin ? "Admin" : "Viewer"}
            </Badge>
          </div>

          {isAdmin && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium mb-3">Invite a team member</p>
                <div className="flex flex-col sm:flex-row gap-2 sm:flex-wrap">
                  <Input
                    placeholder="colleague@university.edu"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full sm:flex-1 sm:min-w-[200px]"
                    onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                  />
                  <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "admin" | "viewer")}>
                    <SelectTrigger className="w-full sm:w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleInvite} disabled={inviting} className="bg-[#0B1834] hover:bg-[#0A152E] text-white w-full sm:w-auto">
                    {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                    {inviting ? "Sending…" : "Send Invite"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Viewers can view all data but cannot add, edit, or delete records.
                </p>
              </div>
            </>
          )}

          <Separator />

          <div>
            <p className="text-sm font-medium mb-3">Members</p>
            {teamLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : teamMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No team members yet.</p>
            ) : (
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex flex-col gap-2 py-3 px-3 rounded-lg bg-muted/40">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0">
                          {(member.name ?? member.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{member.name ?? member.email}</div>
                          {member.name && <div className="text-xs text-muted-foreground">{member.email}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={member.status === "active" ? "default" : "secondary"} className={
                          member.status === "active"
                            ? "bg-green-500/10 text-green-700 border-green-200 text-xs"
                            : "text-xs"
                        }>
                          {member.status === "pending" ? "Pending" : member.role === "admin" ? "Admin" : "Viewer"}
                        </Badge>
                        {member.personas && member.personas.length > 0 && member.personas.map((p) => (
                          <PersonaBadge key={p} persona={p} size="sm" />
                        ))}
                      </div>
                    </div>
                    {isAdmin && member.status === "active" && (
                      <div className="flex items-center gap-2 flex-wrap pl-11">
                        <span className="text-xs text-muted-foreground">Personas:</span>
                        {ALL_PERSONAS.map((p) => {
                          const active = (member.personas ?? []).includes(p);
                          return (
                            <button
                              key={p}
                              disabled={updatingPersonas === member.id}
                              onClick={() => {
                                const next = active
                                  ? (member.personas ?? []).filter((x) => x !== p)
                                  : [...(member.personas ?? []), p];
                                handleUpdatePersonas(member.id, next);
                              }}
                              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                active
                                  ? p === "Finance"
                                    ? "bg-emerald-500/15 text-emerald-700 border-emerald-300 hover:bg-emerald-500/25"
                                    : p === "HR"
                                      ? "bg-blue-500/15 text-blue-700 border-blue-300 hover:bg-blue-500/25"
                                      : p === "SA"
                                        ? "bg-amber-500/15 text-amber-700 border-amber-300 hover:bg-amber-500/25"
                                        : p === "AD"
                                          ? "bg-slate-500/15 text-slate-700 border-slate-300 hover:bg-slate-500/25"
                                          : "bg-purple-500/15 text-purple-700 border-purple-300 hover:bg-purple-500/25"
                                  : "bg-muted text-muted-foreground border-border hover:border-foreground/30"
                              }`}
                            >
                              {updatingPersonas === member.id ? "…" : active ? `✓ ${PERSONA_LABEL[p]}` : PERSONA_LABEL[p]}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {(member.personas ?? []).includes("SA") && (
                      <SportsInput memberId={member.id} disabled={!isAdmin} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Plans Section — hidden entirely for demo orgs (pricing must not
          surface during live sales demos). */}
      {!isDemoOrg && (
      <div>
        <h2 className="text-lg font-semibold mb-1" id="plans">Plans</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Flat platform fee with add-on seats — all paid plans include a 14-day free trial.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map((plan) => {
            const isCurrentPlan = plan.key === currentPlan;
            const isLoading = loadingPlan === plan.key;
            const Icon = plan.icon;

            return (
              <Card
                key={plan.key}
                className={`relative flex flex-col ${isCurrentPlan ? "border-[#0B1834] ring-1 ring-[#0B1834]" : ""}`}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#0B1834] text-white text-xs px-2">Current plan</Badge>
                  </div>
                )}
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${plan.iconColor}`} />
                    {plan.name}
                  </CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="pt-1">
                    <span className="text-2xl font-bold">{plan.price}</span>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 gap-4">
                  <ul className="space-y-2 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {(plan.key === "track" || plan.key === "draft") && !isCurrentPlan && (() => {
                    const meta = PLAN_META[plan.key];
                    const seats = seatsByPlan[plan.key] ?? meta.minSeats;
                    const platformFee = meta.monthlyPrice ?? 0;
                    const perSeatAdd = meta.perSeatMonthly ?? 0;
                    const includedSeats = meta.includedSeats ?? meta.minSeats;
                    const extraSeats = Math.max(0, seats - includedSeats);
                    const total = platformFee + extraSeats * perSeatAdd;
                    const updateSeats = (n: number) => {
                      const clamped = Math.max(meta.minSeats, Math.min(MAX_SEATS, Math.floor(Number.isFinite(n) ? n : meta.minSeats)));
                      setSeatsByPlan((prev) => ({ ...prev, [plan.key]: clamped }));
                    };
                    return (
                      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                          Add-on seats <span className="normal-case font-normal">(${perSeatAdd}/seat/mo)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            aria-label="Decrease seats"
                            onClick={() => updateSeats(seats - 1)}
                            disabled={seats <= meta.minSeats}
                            className="h-8 w-8 rounded-md border border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <Input
                            type="number"
                            min={meta.minSeats}
                            max={MAX_SEATS}
                            value={seats}
                            onChange={(e) => updateSeats(parseInt(e.target.value, 10))}
                            className="h-8 w-16 text-center text-sm font-semibold"
                            aria-label={`${plan.name} total seats`}
                          />
                          <button
                            type="button"
                            aria-label="Increase seats"
                            onClick={() => updateSeats(seats + 1)}
                            disabled={seats >= MAX_SEATS}
                            className="h-8 w-8 rounded-md border border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-600">
                          {extraSeats > 0 ? (
                            <>
                              <span className="font-semibold text-gray-900">${platformFee.toLocaleString()}</span>
                              <span className="text-gray-500"> + {extraSeats} extra × ${perSeatAdd} = </span>
                              <span className="font-semibold text-gray-900">${total.toLocaleString()}/mo</span>
                            </>
                          ) : (
                            <>
                              <span className="text-gray-500">{includedSeats} seats included · </span>
                              <span className="font-semibold text-gray-900">${platformFee.toLocaleString()}/mo</span>
                            </>
                          )}
                        </p>
                      </div>
                    );
                  })()}
                  {plan.key === "enterprise" ? (
                    <Button variant="outline" asChild>
                      <a href="mailto:sales@zindeal.com">Contact Sales</a>
                    </Button>
                  ) : isCurrentPlan ? (
                    <Button variant="secondary" disabled>
                      Active
                    </Button>
                  ) : plan.key === "free" ? (
                    <Button variant="outline" disabled>
                      Free tier
                    </Button>
                  ) : (
                    <Button
                      className="bg-[#0B1834] hover:bg-[#0A152E] text-white"
                      onClick={() => handleUpgrade(plan.key)}
                      disabled={isLoading || loadingPlan !== null}
                    >
                      {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {`Switch to ${plan.name}`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      )}

      {/* Premium feature previews — these are upgrade upsells, so they are
          hidden for demo orgs (billing is handled by the account team). */}
      {!isDemoOrg && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Palette className="h-4 w-4 text-blue-500" />
              Custom branding
            </CardTitle>
            <CardDescription>
              Upload your logo and choose your accent color (Pro+).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LockedPreview feature="branding.custom">
              <div className="space-y-3 p-4">
                <div className="text-sm font-medium">Logo URL</div>
                <Input placeholder="https://yourdept.edu/logo.png" disabled />
                <div className="text-sm font-medium">Primary color</div>
                <Input placeholder="#0066cc" disabled />
                <Button size="sm" disabled className="w-full">Save branding</Button>
              </div>
            </LockedPreview>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Headphones className="h-4 w-4 text-blue-500" />
              Priority support
            </CardTitle>
            <CardDescription>
              Same-day response from our support team (Pro+).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LockedPreview feature="support.priority">
              <div className="space-y-3 p-4">
                <div className="text-sm font-medium">Dedicated support email</div>
                <div className="text-xs font-mono bg-muted p-2 rounded">priority@zindeal.com</div>
                <Button size="sm" disabled className="w-full">Open priority ticket</Button>
              </div>
            </LockedPreview>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4 text-purple-500" />
              SSO / SAML
            </CardTitle>
            <CardDescription>
              Sign in with your institution's identity provider.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LockedPreview feature="auth.sso">
              <div className="space-y-3 p-4">
                <div className="text-sm font-medium">Identity Provider</div>
                <Input placeholder="https://sso.example.edu/saml" disabled />
                <Button size="sm" disabled className="w-full">Connect provider</Button>
              </div>
            </LockedPreview>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Code2 className="h-4 w-4 text-purple-500" />
              Public REST API
            </CardTitle>
            <CardDescription>
              Programmatic access to your Zindeal data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LockedPreview feature="api.public">
              <div className="space-y-3 p-4">
                <div className="text-sm font-medium">API Keys</div>
                <div className="text-xs font-mono bg-muted p-2 rounded">ahub_sk_live_••••••••••••••••</div>
                <Button size="sm" disabled className="w-full">Generate new key</Button>
              </div>
            </LockedPreview>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Compliance & Security */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              Compliance & Security
            </CardTitle>
            <CardDescription>
              Investigate access patterns and review sensitive activity across your organization.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <WLink
              href="/audit-log"
              className="flex items-center justify-between gap-4 rounded-md border bg-muted/30 hover:bg-muted/60 transition-colors p-4 group"
              data-testid="link-audit-log"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-sky-500/10 text-sky-700 flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Audit log</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    See every document download, upload, field change, and deletion across your organization — including IP and user agent for downloads.
                  </p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
            </WLink>

            {/* 2FA status note */}
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4">
              <div className="w-9 h-9 rounded-md bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                <Lock className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Two-factor authentication (2FA)</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Phone number and authenticator-app 2FA are not yet available in the current authentication configuration.
                  This is a platform-level limitation — adding a second email address does not count as 2FA.
                  2FA enforcement has been disabled so admins can access the app normally.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Privacy & Data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy & Data
          </CardTitle>
          <CardDescription>
            Download a copy of all your data, or permanently delete your account in line with GDPR Articles 17 and 20.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Export my data</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {isAdmin
                  ? "Generate a ZIP containing JSON files for your organization, coaches, contracts, insurance, mitigations, comments, audit log, and copies of all uploaded documents."
                  : "Generate a ZIP containing your profile data, comments you've authored, and mentions directed at you."}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleExportData}
              disabled={exporting}
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {exporting ? "Preparing…" : "Export my data"}
            </Button>
          </div>

          {exportResult && exportResult.pending && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm">
              <p className="font-medium text-blue-900">Your export is being prepared.</p>
              <p className="text-blue-800 mt-1">
                Large exports run in the background.
                {exportResult.emailedTo
                  ? <> We'll email the download link to <span className="font-medium">{exportResult.emailedTo}</span> when it's ready (usually within a few minutes). The link expires {new Date(exportResult.expiresAt).toLocaleString()}.</>
                  : <> The link will be available once the export completes. It expires {new Date(exportResult.expiresAt).toLocaleString()}.</>}
              </p>
            </div>
          )}
          {exportResult && !exportResult.pending && (
            <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm">
              <p className="font-medium text-green-900">Your export is ready.</p>
              <p className="text-green-800 mt-1">
                {(exportResult.sizeBytes / (1024 * 1024)).toFixed(2)} MB · Link expires {new Date(exportResult.expiresAt).toLocaleString()}.
                {exportResult.emailedTo && <> A copy was emailed to <span className="font-medium">{exportResult.emailedTo}</span>.</>}
              </p>
              <a
                href={exportResult.downloadUrl}
                className="inline-flex items-center mt-2 text-sm font-medium text-[#0B1834] hover:underline"
              >
                <Download className="h-4 w-4 mr-1" />
                Download ZIP
              </a>
            </div>
          )}

          {isAdmin && (
            <>
              <Separator />
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">Delete my account</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Permanently delete your organization, all coaches, contracts, insurance, mitigations, comments, audit log, and uploaded documents. Cancels your subscription. <span className="font-medium">This cannot be reversed.</span>
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-destructive/50 text-destructive hover:bg-destructive/5 hover:border-destructive"
                  onClick={() => { setDeleteConfirm(""); setDeleteOpen(true); }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete account
                </Button>
              </div>
            </>
          )}

          {!isAdmin && (
            <p className="text-xs text-muted-foreground">
              Only an admin can delete the organization account.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Sample Data */}
      {org?.role === "admin" && (
        <div>
          <h2 className="text-lg font-semibold mb-1">Sample Data</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Load demo coaches, contracts, and risk items to explore the product, then clear them when you're ready to use Zindeal for real.
          </p>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Database className="h-5 w-5 text-[#0B1834]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {onboarding.status?.hasSampleData ? "Sample data is loaded" : "Sample data is not loaded"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {onboarding.status?.hasSampleData
                        ? "5 coaches, 8 contracts, insurance policies, and risk items tagged as samples."
                        : "Add a realistic dataset you can safely explore and remove later."}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {onboarding.status?.hasSampleData ? (
                    <Button
                      variant="outline"
                      className="border-destructive/50 text-destructive hover:bg-destructive/5 hover:border-destructive"
                      disabled={clearingSample}
                      onClick={async () => {
                        if (!confirm("Remove all sample coaches, contracts, insurance, and risk items? This cannot be undone.")) return;
                        setClearingSample(true);
                        const r = await onboarding.clearSampleData();
                        setClearingSample(false);
                        if (r.ok) {
                          toast({ title: "Sample data removed", description: "All sample coaches and related records were cleared." });
                        } else {
                          toast({ title: "Couldn't clear sample data", description: r.error ?? "Try again.", variant: "destructive" });
                        }
                      }}
                      data-testid="button-clear-sample"
                    >
                      {clearingSample ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                      Clear sample data
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={loadingSample}
                      onClick={async () => {
                        setLoadingSample(true);
                        const r = await onboarding.loadSampleData();
                        setLoadingSample(false);
                        if (r.ok) {
                          toast({ title: "Sample data loaded", description: "Explore your demo dataset on the dashboard." });
                        } else {
                          toast({ title: "Couldn't load sample data", description: r.error ?? "Try again.", variant: "destructive" });
                        }
                      }}
                      data-testid="button-load-sample-settings"
                    >
                      {loadingSample ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                      Load sample data
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Product Tour */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5" />
            Product tour
          </CardTitle>
          <CardDescription>
            Replay the guided walkthrough to revisit key features — useful for new teammates or a quick refresher.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Replay product tour</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {onboarding.status?.tourCompletedAt
                  ? `You last completed the tour on ${formatDate(onboarding.status.tourCompletedAt)}.`
                  : "You haven't completed the tour yet."}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                onboarding.startTour();
                toast({ title: "Starting tour", description: "We'll walk you through the key features again." });
              }}
              data-testid="button-replay-tour"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Replay tour
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <div>
        <h2 className="text-lg font-semibold mb-1 text-destructive">Danger Zone</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Actions here cannot be undone. Proceed with care.
        </p>
        <Card className="border-destructive/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Sign out</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Sign out of your Zindeal account on this device.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/5 hover:border-destructive"
                onClick={() => signOut({ redirectUrl: basePath || "/" })}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete account confirmation */}
      <Dialog open={deleteOpen} onOpenChange={(open) => { if (!deleting) setDeleteOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Permanently delete account
            </DialogTitle>
            <DialogDescription>
              This will immediately and permanently delete <span className="font-semibold text-foreground">{org?.institutionName ?? org?.name}</span> along with all coaches, contracts, insurance, mitigations, comments, audit log, and uploaded documents. Your active subscription will be cancelled. <span className="font-medium">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="deleteConfirm" className="text-sm">
              Type <span className="font-semibold">{org?.institutionName ?? org?.name}</span> to confirm:
            </Label>
            <Input
              id="deleteConfirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={org?.institutionName ?? org?.name ?? ""}
              autoComplete="off"
              disabled={deleting}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm.trim() !== (org?.institutionName ?? org?.name ?? "")}
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface InvoiceRow {
  id: string;
  number: string | null;
  created: number;
  total: number;
  currency: string;
  status: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

interface SeatChangeEventRow {
  id: number;
  previousSeats: number;
  newSeats: number;
  proratedAmount: number | null;
  currency: string | null;
  createdAt: string;
}

function formatInvoiceAmount(total: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(total / 100);
  } catch {
    return `${(total / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function invoiceStatusBadge(status: string | null) {
  const s = (status ?? "").toLowerCase();
  if (s === "paid") return { label: "Paid", className: "bg-green-500/10 text-green-700 border-green-200" };
  if (s === "open") return { label: "Open", className: "bg-amber-500/10 text-amber-700 border-amber-200" };
  if (s === "void") return { label: "Void", className: "bg-gray-500/10 text-gray-700 border-gray-200" };
  if (s === "uncollectible") return { label: "Uncollectible", className: "bg-destructive/10 text-destructive border-destructive/20" };
  if (s === "draft") return { label: "Draft", className: "bg-muted text-muted-foreground border-border" };
  return { label: status ?? "—", className: "" };
}

interface BrandingCardProps {
  isAdmin: boolean;
  org: ReturnType<typeof useOrg>["org"];
  onRefetch: () => void;
}

function BrandingCard({ isAdmin, org, onRefetch }: BrandingCardProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [logoUrl, setLogoUrl] = useState(org?.logoUrl ?? "");
  const [primaryColor, setPrimaryColor] = useState(org?.primaryColor ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/org/branding", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ logoUrl: logoUrl || null, primaryColor: primaryColor || null }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error ?? "Failed to save branding");
      }
      toast({ title: "Branding saved", description: "Your logo and color will appear on exported PDFs." });
      onRefetch();
    } catch (err: unknown) {
      toast({ title: "Save failed", description: err instanceof Error ? err.message : "Could not save branding.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const previewColor = primaryColor && /^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : "#0B1834";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Branding
        </CardTitle>
        <CardDescription>
          Set your institution logo and primary color for exported PDFs. Requires a paid plan.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <LockedPreview feature="branding.custom">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="logoUrl">Institution Logo URL</Label>
              <div className="flex gap-3 items-center">
                {logoUrl && /^https?:\/\//.test(logoUrl) && (
                  <img src={logoUrl} alt="Logo preview" className="h-10 w-10 rounded object-contain border" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                )}
                <Input
                  id="logoUrl"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://your-institution.edu/logo.png"
                  disabled={!isAdmin}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                A publicly accessible URL to your institution's logo image. Used on cover pages.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Brand Color</Label>
              <div className="flex gap-3 items-center">
                <div className="h-9 w-9 rounded border flex-shrink-0" style={{ backgroundColor: previewColor }} />
                <Input
                  id="primaryColor"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#0B1834"
                  disabled={!isAdmin}
                  className="flex-1 font-mono"
                  maxLength={7}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Hex color code (e.g. <code>#0B1834</code>). Used as the header and accent color on exported PDFs.
              </p>
            </div>

            {isAdmin && (
              <Button onClick={handleSave} disabled={saving} size="sm">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {saving ? "Saving…" : "Save branding"}
              </Button>
            )}
          </div>
        </LockedPreview>
      </CardContent>
    </Card>
  );
}

interface BillingCardProps {
  org: ReturnType<typeof useOrg>["org"];
  loadingPortal: boolean;
  onManageBilling: () => void;
}

function BillingCard({ org, loadingPortal, onManageBilling }: BillingCardProps) {
  const { getToken } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [seatHistory, setSeatHistory] = useState<SeatChangeEventRow[] | null>(null);
  const [seatHistoryLoading, setSeatHistoryLoading] = useState(false);
  const [seatHistoryError, setSeatHistoryError] = useState<string | null>(null);

  const hasCustomer = Boolean(org?.stripeCustomerId);

  useEffect(() => {
    if (!hasCustomer) {
      setInvoices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setInvoicesLoading(true);
      setInvoicesError(null);
      try {
        const token = await getToken();
        const res = await fetch("/api/billing/invoices", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json() as { invoices?: InvoiceRow[]; error?: string };
        if (!res.ok) throw new Error(data.error ?? "Failed to load invoices");
        if (!cancelled) setInvoices(data.invoices ?? []);
      } catch (err) {
        if (!cancelled) setInvoicesError(err instanceof Error ? err.message : "Failed to load invoices");
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hasCustomer, getToken]);

  useEffect(() => {
    if (!hasCustomer) {
      setSeatHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSeatHistoryLoading(true);
      setSeatHistoryError(null);
      try {
        const token = await getToken();
        const res = await fetch("/api/billing/seat-history", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json() as { events?: SeatChangeEventRow[]; error?: string };
        if (!res.ok) throw new Error(data.error ?? "Failed to load seat history");
        if (!cancelled) setSeatHistory(data.events ?? []);
      } catch (err) {
        if (!cancelled) setSeatHistoryError(err instanceof Error ? err.message : "Failed to load seat history");
      } finally {
        if (!cancelled) setSeatHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hasCustomer, getToken]);

  if (!org) return null;

  const planLabel = org.plan.charAt(0).toUpperCase() + org.plan.slice(1);
  const hasSubscription = Boolean(org.stripeCustomerId);
  const trialEnd = org.lifecycle?.trialEndsAt ? new Date(org.lifecycle.trialEndsAt) : null;
  const isOnTrial = trialEnd ? trialEnd.getTime() > Date.now() : false;
  const cancelling = Boolean(org.lifecycle?.cancelAt);
  const periodEnd = org.stripeCurrentPeriodEnd ? new Date(org.stripeCurrentPeriodEnd) : null;
  const cardBrand = org.stripePaymentBrand;
  const cardLast4 = org.stripePaymentLast4;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Billing
        </CardTitle>
        <CardDescription>
          Manage your subscription, payment method, and download past invoices.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cancelling && periodEnd && (
          <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-destructive">Subscription scheduled for cancellation</p>
              <p className="text-muted-foreground mt-0.5">
                Your subscription will end on {formatDate(org.stripeCurrentPeriodEnd)}. You can resume anytime before then via Manage Billing.
              </p>
            </div>
          </div>
        )}

        {isOnTrial && trialEnd && !cancelling && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
            <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">Free trial active</p>
              <p className="text-amber-800/80 mt-0.5">
                Your trial ends on {formatDate(org.lifecycle?.trialEndsAt)}. After that you'll be billed for the {planLabel} plan.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Current plan</span>
          <Badge variant="secondary" className="capitalize">{planLabel}</Badge>
        </div>

        {(org.plan === "track" || org.plan === "draft") && (() => {
          const used = org.coachCount ?? 0;
          const total = Math.max(1, org.purchasedSeats ?? 1);
          const remaining = Math.max(0, total - used);
          const atLimit = used >= total;
          const nearLimit = !atLimit && remaining <= Math.max(1, Math.ceil(total * 0.1));
          const pct = Math.min(100, Math.round((used / total) * 100));
          const barColor = atLimit ? "bg-destructive" : nearLimit ? "bg-amber-500" : "bg-[#0B1834]";
          return (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Seat usage</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {used} of {total} {total === 1 ? "seat" : "seats"} used
                      {remaining > 0 ? ` · ${remaining} available` : " · no seats remaining"}
                    </p>
                  </div>
                  {hasSubscription && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onManageBilling}
                      disabled={loadingPortal}
                    >
                      {loadingPortal ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      Manage seats
                      <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                    </Button>
                  )}
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full ${barColor} transition-all`}
                    style={{ width: `${pct}%` }}
                    role="progressbar"
                    aria-valuenow={used}
                    aria-valuemin={0}
                    aria-valuemax={total}
                    aria-label={`${used} of ${total} seats used`}
                  />
                </div>
                {atLimit && (
                  <p className="text-xs text-destructive">
                    You've used all your purchased seats. Add more seats to invite additional coaches.
                  </p>
                )}
                {nearLimit && (
                  <p className="text-xs text-amber-700">
                    You're approaching your seat limit. Consider purchasing more seats soon.
                  </p>
                )}
              </div>
            </>
          );
        })()}

        {periodEnd && (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {cancelling ? "Access ends on" : isOnTrial ? "Trial ends on" : "Next billing date"}
              </span>
              <span className="text-sm font-medium">{formatDate(org.stripeCurrentPeriodEnd)}</span>
            </div>
          </>
        )}

        <Separator />
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Payment method</span>
          <span className="text-sm font-medium">
            {cardBrand && cardLast4 ? (
              <span className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="capitalize">{cardBrand}</span>
                <span>•••• {cardLast4}</span>
              </span>
            ) : (
              <span className="text-muted-foreground italic">None on file</span>
            )}
          </span>
        </div>

        <Separator />
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">Invoice history</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Your most recent invoices. Download PDFs for your records.
            </p>
          </div>
          {invoicesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading invoices…
            </div>
          ) : invoicesError ? (
            <div className="text-sm text-destructive py-2">{invoicesError}</div>
          ) : !invoices || invoices.length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground text-center">
              No invoices yet. Once your trial ends or your first payment is processed, invoices will appear here.
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Date</th>
                    <th className="text-left font-medium px-3 py-2">Amount</th>
                    <th className="text-left font-medium px-3 py-2">Status</th>
                    <th className="text-right font-medium px-3 py-2">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const badge = invoiceStatusBadge(inv.status);
                    return (
                      <tr key={inv.id} className="border-t">
                        <td className="px-3 py-2">
                          {new Date(inv.created * 1000).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {formatInvoiceAmount(inv.total, inv.currency)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={badge.className}>
                            {badge.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {inv.invoicePdf ? (
                            <a
                              href={inv.invoicePdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#0B1834] hover:underline"
                            >
                              <Download className="h-3.5 w-3.5" />
                              PDF
                            </a>
                          ) : inv.hostedInvoiceUrl ? (
                            <a
                              href={inv.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#0B1834] hover:underline"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              View
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Separator />
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">Seat history</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Recent seat-count changes and the prorated charge or credit for each.
            </p>
          </div>
          {seatHistoryLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading seat history…
            </div>
          ) : seatHistoryError ? (
            <div className="text-sm text-destructive py-2">{seatHistoryError}</div>
          ) : !seatHistory || seatHistory.length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground text-center">
              No seat changes yet. When you add or remove seats, each change will be recorded here.
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Date</th>
                    <th className="text-left font-medium px-3 py-2">Change</th>
                    <th className="text-right font-medium px-3 py-2">Prorated</th>
                  </tr>
                </thead>
                <tbody>
                  {seatHistory.map((evt) => {
                    const delta = evt.newSeats - evt.previousSeats;
                    const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`;
                    const deltaClass = delta > 0
                      ? "text-emerald-700"
                      : delta < 0
                        ? "text-amber-700"
                        : "text-muted-foreground";
                    let proratedLabel = "—";
                    let proratedClass = "text-muted-foreground";
                    if (evt.proratedAmount !== null && evt.currency) {
                      proratedLabel = formatInvoiceAmount(evt.proratedAmount, evt.currency);
                      if (evt.proratedAmount > 0) proratedClass = "text-foreground";
                      else if (evt.proratedAmount < 0) proratedClass = "text-emerald-700";
                    }
                    return (
                      <tr key={evt.id} className="border-t">
                        <td className="px-3 py-2">
                          {new Date(evt.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-muted-foreground">{evt.previousSeats}</span>
                          <span className="mx-1.5 text-muted-foreground">→</span>
                          <span className="font-medium">{evt.newSeats}</span>
                          {delta !== 0 && (
                            <span className={`ml-2 text-xs font-medium ${deltaClass}`}>
                              ({deltaLabel} {Math.abs(delta) === 1 ? "seat" : "seats"})
                            </span>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${proratedClass}`}>
                          {proratedLabel}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Separator />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Manage billing</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Change plan, update payment method, view invoices, or cancel.
            </p>
          </div>
          {hasSubscription ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onManageBilling}
              disabled={loadingPortal}
            >
              {loadingPortal ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Manage Billing
              <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
            </Button>
          ) : (
            <Button variant="outline" size="sm" asChild>
              <a href="#plans">Choose a plan</a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
